import React, { useContext, useEffect, useState } from 'react';
import { Container, SpaceBetween } from '@amzn/awsui-components-react';
import { CalculationBuilderContext } from '../CalculationBuilderView';
import { TPELoadingSpinner } from 'src/components/shared/TPELoadingSpinner';
import ExecutionGroupSelector from './ExecutionGroupSelector';
import TPEErrorWatcher from 'src/components/shared/TPEErrorWatcher';
import { ACTIONS } from 'src/services/calculation-builder/CalculationBuilderReducer';
import { ExecutionGroupDetails } from 'src/models/calculation-builder/ExecutonGroupDetails';

export default function CLIGroupAssignmentContainer(props: { loading: boolean }) {
    const { loading } = props;
    const { services, calcBuilderState, calcBuilderDispatch } = useContext(CalculationBuilderContext);
    const { calculation, dataSourceRecords } = calcBuilderState;

    const [loadTime, setLoadTime] = useState(null as string | null);
    const [saveExecutionGroupPayload, setSaveExecutionGroupPayload] = useState(null as unknown as any);
    const [executionGroupDetails, setExecutionGroupDetails] = useState(undefined as ExecutionGroupDetails | undefined);
    const [canEditCLIAttributes, setCanEditCLIAttributes] = useState(true);

    const [executionGroupDetailsResult, isGettingExecutionGroupDetails, executionGroupDetailsError] =
        services.calculationsService.getOrAssignExecutionGroup(loadTime, calculation?.calculationNumber);
    const [saveExecutionGroupResult, isSavingExecutionGroup, saveExecutionGroupError] =
        services.calculationsService.saveCalculationExecutionGroup(saveExecutionGroupPayload);
    const [cliExecutionGroups, cliExecutionGroupsLoading, cliExecutionGroupsError] =
        services.calculationsService.getCLIExecutionGroups(loadTime);

    useEffect(() => {
        if (calculation?.calculationNumber) {
            setLoadTime(new Date().toString());
        }
    }, [calculation?.calculationNumber]);

    useEffect(() => {
        if (executionGroupDetailsResult == null) {
            return;
        }
        setExecutionGroupDetails(executionGroupDetailsResult);
        calcBuilderDispatch(ACTIONS.SET_EXECUTION_GROUP_NUMBER.withPayload(executionGroupDetailsResult.executionGroup));
    }, [executionGroupDetailsResult]);

    useEffect(() => {
        if (saveExecutionGroupResult == null) {
            return;
        }
        setExecutionGroupDetails(saveExecutionGroupResult);
        calcBuilderDispatch(ACTIONS.SET_EXECUTION_GROUP_NUMBER.withPayload(saveExecutionGroupResult.executionGroup));
        let successMsg = `Calculation assigned to Group ${saveExecutionGroupResult.executionGroup} successfully.`;
        if (calculation?.workbookId != null) {
            successMsg += ` ${saveExecutionGroupResult.autoDraftsForActiveCalcsCreated} active calculation(s) and `;
            successMsg += `${saveExecutionGroupResult.autoDraftsForPendingReviewCalcsCreated} pending review calculation(s) `;
            successMsg += `in the workbook ${calculation?.workbookId} (${calculation?.workbookName}) were automatically moved to draft status due to this update.`;
        }
        services.messageService.showSuccessAutoDismissBanner(successMsg, 10000);
        calcBuilderDispatch(ACTIONS.CHECK_AND_REFRESH_CALCULATION_INFORMATION);
    }, [saveExecutionGroupResult]);

    useEffect(() => {
        if (saveExecutionGroupError == null) {
            return;
        }
        calcBuilderDispatch(ACTIONS.CHECK_AND_REFRESH_CALCULATION_INFORMATION);
    }, [saveExecutionGroupError]);

    useEffect(() => {
        setCanEditCLIAttributes(!isSavingExecutionGroup);
    }, [isSavingExecutionGroup]);

    const saveExecutionGroup = (executionGroup: string) => {
        setSaveExecutionGroupPayload({
            calculationNumber: calculation?.calculationNumber,
            executionGroup: executionGroup,
        });
    };

    return (
        <Container className="templatesContainer">
            <TPELoadingSpinner loading={loading || isGettingExecutionGroupDetails || cliExecutionGroupsLoading}>
                <ExecutionGroupSelector
                    cliExecutionGroups={cliExecutionGroups || []}
                    executionGroupDetails={executionGroupDetails}
                    canEditCLIAttributes={canEditCLIAttributes}
                    calculation={calculation}
                    saveExecutionGroup={saveExecutionGroup}
                    isSaving={isSavingExecutionGroup}
                    dataSources={dataSourceRecords}
                />
                <TPEErrorWatcher
                    services={services}
                    errors={[executionGroupDetailsError, saveExecutionGroupError, cliExecutionGroupsError]}
                />
            </TPELoadingSpinner>
        </Container>
    );
}
